/**
 * tRPC Provider for Vite + React Router
 * Wraps the app with tRPC and React Query providers
 */
import type { AppRouter } from '@/server/api/root';
import { api, getTRPCClientConfig } from '@/utils/api';
import { captureException } from '@sentry/react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TRPCClientError, type TRPCClientErrorLike } from '@trpc/client';
import { useState, type ReactNode } from 'react';

interface TRPCProviderProps {
    children: ReactNode;
}

const isTRPCError = (error: unknown): error is TRPCClientErrorLike<AppRouter> =>
    error instanceof TRPCClientError;

export function TRPCProvider({ children }: TRPCProviderProps) {
    const [queryClient] = useState(() => {
        const handleApiError = (error: unknown) => {
            if (isTRPCError(error) && error.data?.code === 'UNAUTHORIZED') {
                window.location.reload();
            } else {
                captureException(error);
            }
        };

        const queryCache = new QueryCache({
            onError: handleApiError,
        });
        const mutationCache = new MutationCache({
            onError: handleApiError,
        });

        return new QueryClient({
            queryCache,
            mutationCache,
            defaultOptions: {
                queries: {
                    refetchOnWindowFocus: false,
                },
            },
        });
    });

    const [trpcClient] = useState(() => api.createClient(getTRPCClientConfig()));

    return (
        <api.Provider client={trpcClient} queryClient={queryClient}>
            <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
        </api.Provider>
    );
}
